import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TripComparison } from '@/hooks/use-trip-comparison';
import { useGpsTripDetails } from '@/hooks/use-gps-trips';
import { TripMapView } from '@/components/gps/TripMapView';
import { Badge } from '@/components/ui/badge';
import { Route, Clock, MapPin, Timer, AlertTriangle } from 'lucide-react';

interface TripComparisonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  comparison: TripComparison | null;
}

interface MetricCardProps {
  title: string;
  value: string;
  trend?: number;
  icon?: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, trend, icon }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
        {icon}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-2xl font-bold">{value}</p>
      {trend !== undefined && (
        <p className={`text-xs mt-1 ${trend > 0 ? 'text-orange-500' : 'text-green-500'}`}>
          {trend > 0 ? '+' : ''}{trend.toFixed(1)}
        </p>
      )}
    </CardContent>
  </Card>
);

export const TripComparisonModal: React.FC<TripComparisonModalProps> = ({
  open,
  onOpenChange,
  comparison
}) => {
  const { data: gpsPositions } = useGpsTripDetails(comparison?.gps_trip_id || '');

  if (!comparison) return null;

  const hasGpsData = !!comparison.gps_trip_id;

  const getAdherenceBadge = (score?: number) => {
    if (!score) return <Badge variant="outline">Sem dados GPS</Badge>;
    if (score >= 90) return <Badge className="bg-green-500 hover:bg-green-600">Excelente: {score.toFixed(0)}%</Badge>;
    if (score >= 70) return <Badge className="bg-blue-500 hover:bg-blue-600">Bom: {score.toFixed(0)}%</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-500 hover:bg-yellow-600">Regular: {score.toFixed(0)}%</Badge>;
    return <Badge variant="destructive">Crítico: {score.toFixed(0)}%</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Comparativo de Viagem - {comparison.vehicle_plate}</DialogTitle>
            {getAdherenceBadge(comparison.adherence_score)}
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Resumo</TabsTrigger>
            <TabsTrigger value="map" disabled={!hasGpsData}>Mapa</TabsTrigger>
            <TabsTrigger value="metrics">Métricas Detalhadas</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {/* Tabela comparativa lado a lado */}
            <div className="grid grid-cols-2 gap-4">
              {/* Planejado */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Planejado</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-green-500" />
                      Origem:
                    </p>
                    <p className="text-muted-foreground ml-6">{comparison.planned_origin}</p>
                  </div>
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-red-500" />
                      Destino:
                    </p>
                    <p className="text-muted-foreground ml-6">{comparison.planned_destination}</p>
                  </div>
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Route className="h-4 w-4" />
                      Distância:
                    </p>
                    <p className="text-muted-foreground ml-6">{comparison.planned_distance_km.toFixed(1)} km</p>
                  </div>
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Timer className="h-4 w-4" />
                      Duração estimada:
                    </p>
                    <p className="text-muted-foreground ml-6">{comparison.planned_duration_min} minutos</p>
                  </div>
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Horário de início:
                    </p>
                    <p className="text-muted-foreground ml-6">
                      {new Date(comparison.planned_start).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Realizado */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Realizado</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {hasGpsData ? (
                    <>
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-500" />
                          Origem:
                        </p>
                        <p className="text-muted-foreground ml-6">{comparison.actual_origin_formatted}</p>
                      </div>
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-red-500" />
                          Destino:
                        </p>
                        <p className="text-muted-foreground ml-6">{comparison.actual_destination_formatted}</p>
                      </div>
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <Route className="h-4 w-4" />
                          Distância:
                        </p>
                        <p className="text-muted-foreground ml-6">
                          {comparison.actual_distance_km?.toFixed(1)} km
                          <span className={comparison.distance_variance_km! > 0 ? 'text-orange-500 ml-2' : 'text-green-500 ml-2'}>
                            ({comparison.distance_variance_km! > 0 ? '+' : ''}
                            {comparison.distance_variance_km?.toFixed(1)} km)
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <Timer className="h-4 w-4" />
                          Duração real:
                        </p>
                        <p className="text-muted-foreground ml-6">
                          {comparison.actual_duration_min} minutos
                          <span className={comparison.time_variance_min! > 0 ? 'text-orange-500 ml-2' : 'text-green-500 ml-2'}>
                            ({comparison.time_variance_min! > 0 ? '+' : ''}
                            {comparison.time_variance_min} min)
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Horário real de início:
                        </p>
                        <p className="text-muted-foreground ml-6">
                          {new Date(comparison.actual_start!).toLocaleString('pt-BR')}
                          {Math.abs(comparison.start_delay_min!) > 5 && (
                            <span className="text-orange-500 ml-2">
                              ({comparison.start_delay_min! > 0 ? '+' : ''}
                              {comparison.start_delay_min} min)
                            </span>
                          )}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground italic py-8">
                      <AlertTriangle className="h-5 w-5" />
                      <p>Viagem ainda não realizada ou não detectada pelo GPS</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="map">
            {hasGpsData && gpsPositions && gpsPositions.length > 0 ? (
              <div className="h-[500px]">
                <TripMapView
                  trip={{
                    id: comparison.gps_trip_id!,
                    start_time: comparison.actual_start!,
                    end_time: comparison.actual_end!,
                    distance_km: comparison.actual_distance_km!,
                    duration_sec: (comparison.actual_duration_min || 0) * 60,
                    idle_time_sec: (comparison.actual_idle_time_min || 0) * 60,
                    start_address_formatted: comparison.actual_origin_formatted!,
                    end_address_formatted: comparison.actual_destination_formatted!,
                  } as any}
                  routePoints={gpsPositions}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[500px] text-muted-foreground">
                <div className="text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
                  <p>Dados de GPS não disponíveis</p>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="metrics">
            <div className="grid grid-cols-3 gap-4">
              <MetricCard 
                title="Variação de Distância"
                value={`${comparison.distance_variance_percent?.toFixed(1) || 0}%`}
                trend={comparison.distance_variance_km}
                icon={<Route className="h-4 w-4" />}
              />
              <MetricCard 
                title="Variação de Tempo"
                value={`${Math.abs(comparison.time_variance_min || 0)} min`}
                trend={comparison.time_variance_min}
                icon={<Clock className="h-4 w-4" />}
              />
              <MetricCard 
                title="Tempo Ocioso"
                value={`${comparison.actual_idle_time_min || 0} min`}
                icon={<Timer className="h-4 w-4" />}
              />
            </div>

            {hasGpsData && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-sm">Análise de Pontualidade</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Atraso no início:</span>
                    <span className={`text-sm font-semibold ${comparison.start_delay_min! > 5 ? 'text-orange-500' : 'text-green-500'}`}>
                      {comparison.start_delay_min! > 0 ? '+' : ''}
                      {comparison.start_delay_min} min
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Atraso no término:</span>
                    <span className={`text-sm font-semibold ${comparison.end_delay_min! > 10 ? 'text-orange-500' : 'text-green-500'}`}>
                      {comparison.end_delay_min! > 0 ? '+' : ''}
                      {comparison.end_delay_min} min
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
